import * as THREE from 'three';
import { CLASSICAL } from '../materials';
import { Beam, type ModelProps } from '../parts';

/**
 * Covert units (the spy): sits on y = 0, faces +z, at real proportions
 * (UnitModel scales it). Unlike every other unit it is almost all dark, with
 * only a small scarf in team colour, and it crouches, so at map zoom it reads
 * apart from the upright, team-coloured, staff-carrying scout.
 */

const { SKIN, CLOAK, FELT, METAL, DARK_WOOD } = CLASSICAL;

const G = {
  cloak: new THREE.ConeGeometry(0.14, 0.27, 8),
  shoulders: new THREE.SphereGeometry(0.1, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2),
  head: new THREE.SphereGeometry(0.068, 10, 8),
  // Open at the front (+z, phi = π/2) so the face shows under the brim.
  hood: new THREE.SphereGeometry(
    0.078,
    10,
    6,
    Math.PI / 2 + 0.7,
    Math.PI * 2 - 1.4,
    0,
    Math.PI * 0.62
  ),
  brim: new THREE.CylinderGeometry(0.15, 0.15, 0.012, 14),
  crown: new THREE.CylinderGeometry(0.06, 0.07, 0.07, 10),
  scarf: new THREE.TorusGeometry(0.066, 0.02, 5, 10).rotateX(Math.PI / 2),
  scarfTail: new THREE.BoxGeometry(0.035, 0.1, 0.012),
  boot: new THREE.BoxGeometry(0.045, 0.04, 0.08),
  blade: new THREE.ConeGeometry(0.012, 0.09, 4),
};

// How far the upper body leans forward (radians about x), and where it pivots.
const LEAN = 0.35;
const HIP_Y = 0.06;

/**
 * A crouched figure in a dark hooded cloak under a wide-brimmed felt hat, a
 * small team-coloured scarf at the throat with its tail trailing behind, and
 * a short dagger held low in front.
 */
export function Spy({ accent }: ModelProps) {
  return (
    <>
      {[-0.05, 0.05].map((x) => (
        <mesh key={x} geometry={G.boot} material={FELT} position={[x, 0.02, 0.04]} />
      ))}
      <group position={[0, HIP_Y, 0]} rotation={[LEAN, 0, 0]}>
        <mesh geometry={G.cloak} material={CLOAK} position={[0, 0.07, 0]} />
        <mesh geometry={G.shoulders} material={CLOAK} position={[0, 0.16, 0]} />
        <mesh geometry={G.scarf} material={accent} position={[0, 0.2, 0]} />
        <mesh
          geometry={G.scarfTail}
          material={accent}
          position={[0.03, 0.15, -0.075]}
          rotation={[-0.5, 0, 0.2]}
        />
        <mesh geometry={G.head} material={SKIN} position={[0, 0.26, 0.01]} />
        <mesh geometry={G.hood} material={CLOAK} position={[0, 0.265, -0.008]} />
        <group position={[0, 0.315, 0]} rotation={[-0.15, 0, 0]}>
          <mesh geometry={G.brim} material={FELT} />
          <mesh geometry={G.crown} material={FELT} position={[0, 0.035, 0]} />
        </group>
        <Beam from={[0.09, 0.16, 0.02]} to={[0.08, 0.05, 0.1]} material={CLOAK} thickness={0.04} />
        <Beam
          from={[0.08, 0.04, 0.1]}
          to={[0.08, 0.035, 0.13]}
          material={DARK_WOOD}
          thickness={0.018}
        />
        <mesh
          geometry={G.blade}
          material={METAL}
          position={[0.08, 0.03, 0.18]}
          rotation={[Math.PI / 2, 0, 0]}
        />
      </group>
    </>
  );
}

/**
 * Owner-only markers over a spy. They are not 3D: the UI draws them as `Html`
 * badges (like the rank and shelter badges), 👁 while stealthed and 🎭 while
 * disguised. Rivals never get either.
 */
export const SPY_MARKERS = { stealth: '👁', disguised: '🎭' } as const;
